import type { KyInstance } from "ky";
import type { UploadedFileRef } from "./applications.js";
import {
  toSearchParams,
  type PaginatedRequest,
  type PaginatedResponse,
} from "./pagination.js";
import type { LlmTokenUsage } from "./practitioners-public.js";
import type { TaxonomyOption } from "./taxonomy.js";

export type IsoDateTime = string;

export interface TextData {
  type: "quilljs";
  data: object;
}

export enum PractitionerType {
  Psychologist = "psychologist",
  Psychiatrist = "psychiatrist",
  Counsellor = "counsellor",
  SocialWorker = "social_worker",
  ArtTherapist = "art_therapist",
}

/**
 * Which `registeredBody` taxonomy slugs a practitioner type may register with, in Australia.
 *
 * Code-controlled for the same reason `PractitionerType` is: this is a rule, not a list. Only
 * psychology and psychiatry are statutorily registered, and under different Ahpra boards — the rest
 * are self-regulated by their professional associations. The membership bodies sit alongside the
 * statutory one rather than replacing it: an APS membership is not a psychology registration, so a
 * psychologist who holds both records two credentials.
 *
 * An admin adding a body has to be given a type here before it can be picked — deliberate. A body
 * nobody is allowed to hold would otherwise sit in the list looking selectable.
 */
export const REGISTERED_BODIES_BY_TYPE: Record<PractitionerType, string[]> = {
  [PractitionerType.Psychologist]: ["psychology-board", "aps"],
  [PractitionerType.Psychiatrist]: ["medical-board", "ranzcp"],
  [PractitionerType.Counsellor]: ["aca", "pacfa", "ccaa", "arcap"],
  [PractitionerType.SocialWorker]: ["aasw"],
  [PractitionerType.ArtTherapist]: ["anzacata", "pacfa"],
};

/** Whether `slug` is a body the given type may register with. An unknown type allows nothing. */
export const isRegisteredBodyAllowed = (type: PractitionerType, slug: string): boolean =>
  (REGISTERED_BODIES_BY_TYPE[type] ?? []).includes(slug);

export type SessionFormat = "in-person" | "telehealth" | "home-visit";

export type Gender = "male" | "female";

export type Weekday =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export type TimeOfDay = string;

export interface TimeRange {
  start: TimeOfDay;
  end: TimeOfDay;
}

/** Partial on purpose: a profile with nothing set carries `{}`, not seven empty days. */
export type WeeklyAvailability = Partial<Record<Weekday, TimeRange[]>>;

/** A document read back after upload — a URL the caller may fetch, plus its original name. */
export interface StoredFileDto {
  url: string;
  filename: string;
}

export interface PractitionerEducationDto {
  id: string;
  university: string;
  degree: string;
  fieldOfStudy?: string;
  yearStart: IsoDateTime;
  yearEnd?: IsoDateTime;
}

/**
 * `unverified` is a credential nobody has checked yet and whose licence paperwork is missing — what
 * the import from the old site creates. Completing it (number, dates, document) moves it to
 * `pending_review` for an admin, exactly like a renewal.
 */
export type PractitionerCredentialStatus =
  | "unverified"
  | "active"
  | "expired"
  | "pending_review"
  | "rejected";

export interface PractitionerCredentialDto {
  id: string;
  type: PractitionerType;
  registeredBody: TaxonomyOption;
  psychologyEndorsements: TaxonomyOption[];
  /** Absent only while `status` is `unverified`, as are the dates and the document. */
  registrationNumber?: string;
  yearOfRegistration?: IsoDateTime;
  licenceExpiryDate?: IsoDateTime;
  licenceDocument?: StoredFileDto;
  status: PractitionerCredentialStatus;
  reviewedAt?: IsoDateTime;
}

export interface PractitionerCertificationDto {
  id: string;
  name: string;
  issuingBody?: string;
  yearQualified: IsoDateTime;
  file?: StoredFileDto;
}

export interface PractitionerAddressDto {
  id: string;
  countryCode: string;
  administrativeAreaLevel1?: string;
  administrativeAreaLevel2?: string;
  postcode?: string;
  locationName: string;
  formattedAddress: string;
  lat?: number;
  lng?: number;
}

/**
 * Where a live profile sits in its lifecycle, derived rather than stored: `licence_valid_until`
 * against `now()`, then `unlisted_at`. An expired licence wins over a manual unlist because it is
 * the reason an admin needs to act. A legacy profile (`isLegacy`) skips the licence check: it is
 * `approved` unless unlisted.
 */
export type PractitionerLifecycle = "approved" | "expired" | "unlisted";

/**
 * A practitioner as the practitioner themselves and an admin read them — everything the public
 * profile carries plus the contact details, licence paperwork and listing state it withholds.
 */
export interface PractitionerProfileDto {
  id: string;
  username: string;

  firstName: string;
  lastName: string;
  title?: TaxonomyOption;

  profilePhotoUrl?: string;

  focusAreas: TaxonomyOption[];
  otherFocusAreas: TaxonomyOption[];
  therapeuticApproaches: TaxonomyOption[];
  culturalBackground: TaxonomyOption[];
  languages: TaxonomyOption[];
  ageGroups: TaxonomyOption[];
  clientTypes: TaxonomyOption[];
  fundingPathways: TaxonomyOption[];
  thirdPartySchemes: TaxonomyOption[];

  credentials: PractitionerCredentialDto[];
  education: PractitionerEducationDto[];
  certifications: PractitionerCertificationDto[];

  derivedYearsOfExperience: number;
  careerBreakYears?: number;

  about?: TextData;
  /** The old site's bio as plain text, on legacy profiles only. Read-only: no request writes it.
   *  The public profile shows it while `about` is empty. */
  legacyAbout?: string;

  /** Empty, like `timezone`, `phoneE164` and `gender` absent, only on a legacy profile. */
  sessionFormats: SessionFormat[];
  practiceAddresses: PractitionerAddressDto[];
  timezone?: string;
  availability: WeeklyAvailability;

  phoneE164?: string;
  primaryEmail: string;
  websiteUrl?: string;

  gender?: Gender;
  isAcceptingNewClients: boolean;

  lifecycle: PractitionerLifecycle;
  /** Migrated from the old site rather than accepted from an application. Set by the import only. */
  isLegacy: boolean;
  listedAt?: IsoDateTime;
  unlistedAt?: IsoDateTime;
  licenceValidUntil?: IsoDateTime;

  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export interface SavePractitionerAddress {
  id?: string;
  countryCode?: string;
  administrativeAreaLevel1?: string;
  administrativeAreaLevel2?: string;
  postcode?: string;
  locationName?: string;
  formattedAddress?: string;
  lat?: number;
  lng?: number;
}

/**
 * A licence date moving, and nothing else.
 *
 * `id` names a credential the practitioner already holds — this cannot add or remove one, because a
 * credential is what an admin verified. For the same reason the registering body is not here, and
 * the registration number only for an `unverified` credential: those two *are* the licence's
 * identity, and changing either would make the verified row describe a different licence. Renewal
 * moves the dates and, with them, the document.
 *
 * Completing an `unverified` credential needs all four of `registrationNumber`,
 * `yearOfRegistration`, `licenceExpiryDate` and `licenceDocumentId` in the one save.
 */
export interface SavePractitionerCredential {
  id: string;
  /** Accepted only while the credential is `unverified`. */
  registrationNumber?: string;
  yearOfRegistration?: IsoDateTime;
  licenceExpiryDate?: IsoDateTime;
  /** From `POST /practitioners/@me/documents`. Absent keeps the licence already on file. */
  licenceDocumentId?: string;
  /** `psychologyEndorsements` slugs, replacing the set. Accepted only on a legacy practitioner's
   *  psychologist licence — anyone else's endorsements are fixed when the licence is verified. */
  psychologyEndorsements?: string[];
}

/**
 * `POST /practitioners/@me/credentials`: a licence a legacy practitioner adds themselves — one the
 * import could not bring across. All of it at once, as on an application; it then waits in
 * `pending_review` for the team to verify. Nobody else adds licences after their application.
 */
export interface AddPractitionerCredentialRequest {
  type: PractitionerType;
  /** A `registeredBody` slug allowed for `type` (see `isRegisteredBodyAllowed`). */
  registeredBody: string;
  /** `psychologyEndorsements` slugs; only on a psychologist licence. */
  psychologyEndorsements?: string[];
  registrationNumber: string;
  yearOfRegistration: IsoDateTime;
  licenceExpiryDate: IsoDateTime;
  /** From `POST /practitioners/@me/documents`. */
  licenceDocumentId: string;
}

/** Unlike its application counterpart every field a live row needs is required — a profile's
 *  education rows are past the stage where a half-filled one is a legitimate state. */
export interface SavePractitionerEducation {
  university: string;
  degree: string;
  fieldOfStudy?: string;
  yearStart: IsoDateTime;
  yearEnd?: IsoDateTime;
}

export interface SavePractitionerCertification {
  /** Echoed back so a save carrying no new upload keeps the certificate already attached. */
  id?: string;
  name: string;
  issuingBody?: string;
  yearQualified: IsoDateTime;
  fileId?: string;
}

/**
 * The body of `PATCH /practitioners/@me` — everything a practitioner may keep current about
 * themselves. Taxonomy selections are **slugs**, the same ones `GET /taxonomy` hands the picker.
 * An absent key is left alone; a present one replaces, so an empty array clears.
 *
 * What is deliberately *not* here, and why:
 * - `username` — the public URL a practitioner is linked by; renaming would break every link to them.
 * - `primaryEmail` — the account's sign-in address, which changes through the auth flow that can
 *   verify it, not through a profile patch.
 * - `derivedYearsOfExperience` and `licenceValidUntil` — read off the credentials, recomputed on
 *   every write that touches them.
 * - A credential's `status`, `lifecycle`, `listedAt` / `unlistedAt` — an admin's call, not the
 *   subject's.
 * - A credential's body and registration number — see `SavePractitionerCredential`.
 *
 * The profile photo has its own endpoint, since it is a file upload rather than a field.
 */
export interface SavePractitionerProfileRequest {
  firstName?: string;
  lastName?: string;
  title?: string;
  gender?: Gender;

  phoneE164?: string;
  websiteUrl?: string;

  focusAreas?: string[];
  otherFocusAreas?: string[];
  therapeuticApproaches?: string[];
  culturalBackground?: string[];
  languages?: string[];
  ageGroups?: string[];
  clientTypes?: string[];
  fundingPathways?: string[];
  thirdPartySchemes?: string[];

  credentials?: SavePractitionerCredential[];
  education?: SavePractitionerEducation[];
  certifications?: SavePractitionerCertification[];

  careerBreakYears?: number;

  /** `null` clears the stored document; an absent key leaves it alone. The service unwraps the
   *  envelope to the bare delta and writes `NULL` for either nullish case. */
  about?: TextData | null;

  sessionFormats?: SessionFormat[];
  practiceAddresses?: SavePractitionerAddress[];
  timezone?: string;
  availability?: WeeklyAvailability;

  isAcceptingNewClients?: boolean;
}

/**
 * The ceiling on a practitioner's About, shared so the editor's word counter, the profile
 * validator and the generator's prompt all agree on one number.
 */
export const ABOUT_WORD_MAX = 200;

/**
 * Asks the API to draft an About from the practitioner's own profile data.
 *
 * `hint` is the line or two the practitioner writes about themselves before pressing
 * "Generate About". Omitting it is the "Surprise me" path: the draft is then built from the
 * profile alone.
 */
export interface GenerateAboutRequest {
  hint?: string;
}

/**
 * A draft About, plus what it cost to produce. Nothing is persisted — the practitioner reviews
 * the draft in the editor and saves it through the usual profile PATCH.
 */
export interface GeneratedAboutDto {
  about: TextData;
  model: string;
  durationMs: number;
  costUsd: number;
  usage: LlmTokenUsage;
  generationsRemaining: number;
}

export type GenerateAboutEvent =
  | { type: "status"; step: "reading-tone" | "writing" | "rewriting" }
  | { type: "delta"; text: string }
  | { type: "done"; result: GeneratedAboutDto }
  | { type: "error"; message: string };

export class GenerateAboutStreamError extends Error {
  override name = "GenerateAboutStreamError";
}

export interface GenerateAboutOptions {
  onEvent?: (event: Exclude<GenerateAboutEvent, { type: "done" | "error" }>) => void;
  signal?: AbortSignal;
}

async function* readEvents<T>(response: Response): AsyncGenerator<T> {
  if (!response.body) return;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });

    let boundary: number;
    while ((boundary = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);

      for (const line of block.split("\n")) {
        if (line.startsWith("data: ")) yield JSON.parse(line.slice(6)) as T;
      }
    }

    if (done) return;
  }
}

/** Columns `GET /admin/practitioners` will sort on. Anything else is rejected by the API. */
export type PractitionerSortKey =
  | "fullName"
  | "licenceValidUntil"
  | "listedAt"
  | "inquiryCount";

/**
 * One row of an admin practitioner list. Deliberately not a `PractitionerProfileDto` — the tables
 * draw five columns and have no use for the profile.
 */
export interface AdminPractitionerListItem {
  id: string;
  username: string;
  fullName: string;
  email: string;
  practitionerTypes: PractitionerType[];
  lifecycle: PractitionerLifecycle;
  licenceValidUntil: IsoDateTime | null;
  listedAt: IsoDateTime | null;
  /** Set while an admin has pulled them off the directory. Distinct from `lifecycle`, which an
   *  expired licence outranks — an expired practitioner may or may not also be unlisted. */
  unlistedAt: IsoDateTime | null;
  /** Every inquiry the directory has ever sent them, however it was later triaged. */
  inquiryCount: number;
  credentialsAwaitingReview: number;
  isLegacy: boolean;
}

export interface ListAdminPractitionersQuery extends PaginatedRequest {
  sortBy?: PractitionerSortKey;
  /** Narrows to one lifecycle state. Omitted, every promoted practitioner comes back. */
  lifecycle?: PractitionerLifecycle;
  credentialStatus?: PractitionerCredentialStatus;
}

/** Every taxonomy filter takes slugs — the same slugs `GET /taxonomy` hands the picker. */
export interface ListPractitionersQuery extends PaginatedRequest {
  lat?: number;
  lng?: number;
  radiusMetres?: number;
  gender?: Gender[];
  practitionerType?: PractitionerType[];
  sessionFormat?: SessionFormat[];
  /** Matches either focus-area list: the two share a vocabulary, so one filter covers both. */
  focusAreas?: string[];
  therapeuticApproaches?: string[];
  culturalBackground?: string[];
  languages?: string[];
  ageGroups?: string[];
  clientTypes?: string[];
  fundingPathways?: string[];
  thirdPartySchemes?: string[];
  weekday?: Weekday;
  after?: TimeOfDay;
  before?: TimeOfDay;
}

export class PractitionerApi {
  constructor(private readonly http: KyInstance) { }

  mine = (): Promise<PractitionerProfileDto> =>
    this.http.get("practitioners/@me").json<PractitionerProfileDto>();

  updateProfile = (patch: SavePractitionerProfileRequest): Promise<PractitionerProfileDto> =>
    this.http.patch("practitioners/@me", { json: patch }).json<PractitionerProfileDto>();

  addCredential = (credential: AddPractitionerCredentialRequest): Promise<PractitionerProfileDto> =>
    this.http.post("practitioners/@me/credentials", { json: credential }).json<PractitionerProfileDto>();

  uploadPhoto = (file: Blob): Promise<PractitionerProfileDto> => {
    const body = new FormData();
    body.set("file", file);

    return this.http.post("practitioners/@me/photo", { body }).json<PractitionerProfileDto>();
  };

  /** Drafts an About. The response is not saved — patch it back if the practitioner keeps it. */
  generateAbout = async (
    body: GenerateAboutRequest = {},
    { onEvent, signal }: GenerateAboutOptions = {},
  ): Promise<GeneratedAboutDto> => {
    const response = await this.http.post("practitioners/@me/about", { json: body, signal, timeout: false });

    for await (const event of readEvents<GenerateAboutEvent>(response)) {
      if (event.type === "done") return event.result;
      if (event.type === "error") throw new GenerateAboutStreamError(event.message);
      onEvent?.(event);
    }

    throw new GenerateAboutStreamError("The draft stopped before it finished. Please try again.");
  };

  /** Two-step, like the application's: this returns a storage id, and only that id is patched. */
  uploadDocument = (file: Blob): Promise<UploadedFileRef> => {
    const body = new FormData();
    body.set("file", file);

    return this.http.post("practitioners/@me/documents", { body }).json<UploadedFileRef>();
  };

  adminList = (
    query: ListAdminPractitionersQuery = {},
  ): Promise<PaginatedResponse<AdminPractitionerListItem>> =>
    this.http
      .get("admin/practitioners", { searchParams: toSearchParams({ ...query }) })
      .json<PaginatedResponse<AdminPractitionerListItem>>();

  adminById = (id: string): Promise<PractitionerProfileDto> =>
    this.http
      .get(`admin/practitioners/${encodeURIComponent(id)}`)
      .json<PractitionerProfileDto>();

  /** Pulls a practitioner off the directory. The profile is kept — `adminRelist` puts it back.
   *  Both answer with an empty body, so neither parses one. */
  adminUnlist = async (id: string): Promise<void> => {
    await this.http.post(`admin/practitioners/${encodeURIComponent(id)}/unlist`);
  };

  adminRelist = async (id: string): Promise<void> => {
    await this.http.post(`admin/practitioners/${encodeURIComponent(id)}/relist`);
  };

  adminApproveCredential = (
    id: string,
    credentialId: string,
  ): Promise<PractitionerProfileDto> =>
    this.http
      .post(
        `admin/practitioners/${encodeURIComponent(id)}/credentials/${encodeURIComponent(credentialId)}/approve`,
      )
      .json<PractitionerProfileDto>();

  adminRejectCredential = (
    id: string,
    credentialId: string,
  ): Promise<PractitionerProfileDto> =>
    this.http
      .post(
        `admin/practitioners/${encodeURIComponent(id)}/credentials/${encodeURIComponent(credentialId)}/reject`,
      )
      .json<PractitionerProfileDto>();
}
